import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeveloperModeRoundedIcon from "@mui/icons-material/DeveloperModeRounded";
import { ownerApi } from "../../../api/owner";

const STATUS_LABEL = {
  queued: "En cola",
  planning: "Planificando",
  inspecting: "Inspeccionando",
  patching: "Aplicando patch",
  testing: "Testeando",
  verifying: "Verificando",
  passed: "Aprobado",
  failed: "Falló",
  blocked: "Bloqueado",
  cancelled: "Cancelado",
};

/**
 * WP-OWNER-PROTOCOL
 * Role: isolated UI for development-agent runs.
 * Authority: visualization/control plane only; backend run record is authoritative.
 * Inputs: explicit development objective from the owner.
 * Outputs: create/select/refresh development runs.
 * Side effects: never mutates CRM, WhatsApp, finance or project entities.
 * Idempotency: context refresh replaces a read-only projection.
 * Autonomy: propose
 * Test status: STATIC_PENDING · INTEGRATION_PENDING · E2E_PENDING
 * Circuit: objective -> run -> context -> future bridge -> evidence -> result
 */
export default function OwnerDevAgentPage() {
  const [runs, setRuns] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [objective, setObjective] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const selected = useMemo(() => runs.find((run) => String(run?._id) === String(selectedId)) || runs[0] || null, [runs, selectedId]);

  const loadRuns = useCallback(async () => {
    setError("");
    try {
      const response = await ownerApi.devAgentRuns({ limit: 60 });
      const next = Array.isArray(response?.runs) ? response.runs : [];
      setRuns(next);
      setSelectedId((current) => current || String(next[0]?._id || ""));
    } catch (err) {
      setError(err?.message || "No se pudieron cargar los runs del Dev Agent.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRuns(); }, [loadRuns]);

  const createRun = async () => {
    const clean = objective.trim();
    if (!clean || creating) return;
    setCreating(true);
    setError("");
    try {
      const response = await ownerApi.createDevAgentRun({
        objective: clean,
        scope: {
          targets: ["backend", "panel"],
          maxChangedFiles: 5,
          allowPatch: false,
          allowShell: false,
          allowBrowser: false,
        },
      });
      const run = response?.run;
      if (run?._id) {
        setRuns((current) => [run, ...current.filter((item) => String(item?._id) !== String(run._id))]);
        setSelectedId(String(run._id));
        setObjective("");
      } else {
        await loadRuns();
      }
    } catch (err) {
      setError(err?.message || "No se pudo crear el run.");
    } finally {
      setCreating(false);
    }
  };

  const refreshContext = async () => {
    if (!selected?._id || refreshing) return;
    setRefreshing(true);
    setError("");
    try {
      const response = await ownerApi.refreshDevAgentContext(selected._id);
      const updated = response?.run;
      if (updated?._id) {
        setRuns((current) => current.map((item) => String(item?._id) === String(updated._id) ? updated : item));
      }
    } catch (err) {
      setError(err?.message || "No se pudo actualizar el contexto.");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, pb: { xs: 10, sm: 3 }, width: "100%" }}>
      <Stack spacing={2.2}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={1.5}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <DeveloperModeRoundedIcon color="primary" />
              <Typography variant="h5" fontWeight={950}>Owner Dev Agent</Typography>
              <Chip size="small" label="PATCH 01" />
            </Stack>
            <Typography color="text.secondary" sx={{ mt: .6, maxWidth: 850 }}>
              Plano aislado de desarrollo. Crea objetivos, conserva contexto y evidencia. En este corte todavía no ejecuta shell, patches ni navegador.
            </Typography>
          </Box>
          <Button startIcon={<RefreshRoundedIcon />} onClick={loadRuns} disabled={loading}>Actualizar</Button>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}
        <Alert severity="info">
          Bridge local: <strong>no conectado todavía</strong>. Este módulo no escribe en CRM, WhatsApp, proyectos ni finanzas.
        </Alert>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Stack spacing={1.2}>
            <Typography fontWeight={900}>Nuevo objetivo de desarrollo</Typography>
            <TextField
              multiline
              minRows={3}
              value={objective}
              onChange={(event) => setObjective(event.target.value)}
              placeholder="Ej: No aparecen los mensajes históricos de WhatsApp después de conectar Baileys. Diagnosticar sin romper el flujo actual."
              fullWidth
            />
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
              <Typography variant="caption" color="text.secondary">Scope inicial: backend + panel · máximo 5 archivos · sólo lectura/diagnóstico.</Typography>
              <Button variant="contained" startIcon={creating ? <CircularProgress size={16} /> : <AddRoundedIcon />} onClick={createRun} disabled={!objective.trim() || creating}>
                Crear run
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems="stretch">
          <Paper variant="outlined" sx={{ width: { xs: "100%", lg: 330 }, borderRadius: 3, overflow: "hidden" }}>
            <Box sx={{ p: 1.5 }}><Typography fontWeight={900}>Runs</Typography></Box>
            <Divider />
            {loading ? <Box sx={{ p: 3, textAlign: "center" }}><CircularProgress size={24} /></Box> : null}
            {!loading && !runs.length ? <Typography color="text.secondary" sx={{ p: 2 }}>Todavía no hay runs.</Typography> : null}
            <Stack divider={<Divider flexItem />}>
              {runs.map((run) => {
                const active = String(selected?._id) === String(run?._id);
                return <Button key={run._id} onClick={() => setSelectedId(String(run._id))} sx={{ justifyContent: "flex-start", textAlign: "left", px: 1.5, py: 1.3, borderRadius: 0, bgcolor: active ? "action.selected" : "transparent", textTransform: "none" }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={900} noWrap>{run.title}</Typography>
                    <Stack direction="row" spacing={.6} sx={{ mt: .55 }} flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={STATUS_LABEL[run.status] || run.status} />
                      <Chip size="small" variant="outlined" label={run.testStatus || "NOT_TESTED"} />
                    </Stack>
                  </Box>
                </Button>;
              })}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, p: 2, borderRadius: 3 }}>
            {!selected ? <Typography color="text.secondary">Seleccioná o creá un run.</Typography> : <Stack spacing={2}>
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={1}>
                <Box>
                  <Typography variant="h6" fontWeight={950}>{selected.title}</Typography>
                  <Typography color="text.secondary" sx={{ whiteSpace: "pre-wrap", mt: .5 }}>{selected.objective}</Typography>
                </Box>
                <Button variant="outlined" startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshRoundedIcon />} onClick={refreshContext} disabled={refreshing}>Refrescar contexto</Button>
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`Estado: ${STATUS_LABEL[selected.status] || selected.status}`} />
                <Chip variant="outlined" label={`Test: ${selected.testStatus}`} />
                <Chip variant="outlined" label={`Máx. archivos: ${selected?.scope?.maxChangedFiles ?? 5}`} />
                <Chip variant="outlined" label="Git push: bloqueado" />
              </Stack>

              <Divider />
              <Box>
                <Typography fontWeight={900} sx={{ mb: 1 }}>Plan</Typography>
                <Stack spacing={.8}>
                  {(selected.plan || []).map((step, index) => <Paper key={`${step.key}-${index}`} variant="outlined" sx={{ p: 1.2, borderRadius: 2 }}>
                    <Stack direction="row" justifyContent="space-between" gap={1}>
                      <Typography fontWeight={800}>{index + 1}. {step.label}</Typography>
                      <Chip size="small" label={step.status} />
                    </Stack>
                    {step.note ? <Typography variant="body2" color="text.secondary" sx={{ mt: .4 }}>{step.note}</Typography> : null}
                  </Paper>)}
                </Stack>
              </Box>

              <Box>
                <Typography fontWeight={900} sx={{ mb: 1 }}>Contexto</Typography>
                <Stack direction="row" spacing={.7} flexWrap="wrap" useFlexGap>
                  {Object.entries(selected?.context?.sources || {}).map(([key, enabled]) => <Chip key={key} size="small" color={enabled ? "success" : "default"} variant={enabled ? "filled" : "outlined"} label={`${key}: ${enabled ? "sí" : "pendiente"}`} />)}
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Generado: {selected?.context?.generatedAt ? new Date(selected.context.generatedAt).toLocaleString() : "sin snapshot"}
                </Typography>
              </Box>

              <Box>
                <Typography fontWeight={900} sx={{ mb: 1 }}>Evidencia</Typography>
                {(selected.evidence || []).length ? <Stack spacing={.7}>{selected.evidence.map((item, index) => <Typography key={index} variant="body2">• {item.label || item.kind} · {item.status}</Typography>)}</Stack> : <Typography color="text.secondary">Todavía no hay evidencia.</Typography>}
              </Box>
            </Stack>}
          </Paper>
        </Stack>
      </Stack>
    </Box>
  );
}
